import type { Signal } from '@preact/signals-react';
import { useSignalEffect } from '@preact/signals-react';
import clsx from 'clsx';
import { cloneDeep, debounce, isEqual, throttle } from 'lodash';
import type { Key, MouseEvent } from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OdinDataRetrieval } from '../../common/data-retrieval/data-retrieval-interfaces';
import useTestTag from '../../common/hooks/use-test-tag';
import { OverlayScrollDetectionType } from '../common/overlay-panel/overlay-panel.props';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
} from '../continuous-scroller/continuous-scroller-types';
import { ContinuousScrollerSortDirection } from '../continuous-scroller/continuous-scroller-types';
import OdinIcon, { OdinIconSize } from '../odin-icon/odin-icon.component';
import OdinIconMenu from '../odin-icon-menu/odin-icon-menu.component';
import type { OdinMenuItem } from '../odin-icon-menu/odin-icon-menu-item.component';
import { ABSOLUTE_MIN_COLUMN_WIDTH_PX, getColumnDefaultWidth } from './data-grid-functions';
import type { DataGridColumnSettings } from './data-grid-settings.interface';
import DataGridDateFilter from './filters/data-grid-date-filter.component';
import DataGridDropDownFilter from './filters/data-grid-drop-down-filter.component';
import DataGridNumericFilter from './filters/data-grid-numeric-filter.component';
import DataGridSearchFilter from './filters/data-grid-search-filter.component';
import { changeSignalColumnSize } from './functions/data-grid-signal-functions';

export interface DataGridColumnProps {
  columns: Signal<Array<DataGridColumnSettings>>;
  rowWidth: Signal<number>;
  indicatorRowWidth: Signal<number>;
  availableWidth: Signal<number>;
  column: DataGridColumnSettings;
  search?: OdinDataRetrieval;
  filterSignal: Signal<Array<ContinuousScrollerFilter>>;
  sortSignal: Signal<Array<ContinuousScrollerSort>>;
  draggable?: boolean;
  scrollDetection?: OverlayScrollDetectionType;
  onColumnDragStart?: (column: DataGridColumnSettings) => void;
  onColumnDragEnd?: (column: DataGridColumnSettings) => void;
  forceResetColumnMap: Map<Key, boolean>;
}

export default function DataGridColumn({
  columns,
  rowWidth,
  indicatorRowWidth,
  availableWidth,
  column,
  search,
  filterSignal,
  sortSignal,
  draggable = true,
  scrollDetection = OverlayScrollDetectionType.Parent,
  onColumnDragStart,
  onColumnDragEnd,
  forceResetColumnMap,
}: DataGridColumnProps) {
  let dragStartPosition = 0;
  let mouseCurrentPosition = 0;

  const [startingColumnWidth, setStartingColumnWidth] = useState(column.width || getColumnDefaultWidth(column));
  const [columnWidth, setColumnWidth] = useState(startingColumnWidth);
  const [isDragging, setIsDragging] = useState(false);

  const { t } = useTranslation();
  const columnId = useId();

  const sortAscMenuItem = useRef<OdinMenuItem>({
    name: 'sortAsc',
    itemIconName: 'SortAsc',
    menuItemText: t('sort-asc', 'Sort Asc'),
  });
  const sortDescMenuItem = useRef<OdinMenuItem>({
    name: 'sortDesc',
    itemIconName: 'SortDesc',
    menuItemText: t('sort-desc', 'Sort Desc'),
  });
  const addSortMenuItems = useRef<Array<OdinMenuItem>>([
    {
      name: 'addSortAsc',
      itemIconName: 'SortAsc',
      menuItemText: t('sort-asc-add', 'Add Sort Asc'),
    },
    {
      name: 'addSortDesc',
      itemIconName: 'SortDesc',
      menuItemText: t('sort-desc-add', 'Add Sort Desc'),
    },
  ]);
  const newSortMenuItems = useRef<Array<OdinMenuItem>>([
    {
      name: 'newSortAsc',
      itemIconName: 'SortAsc',
      menuItemText: t('sort-new-asc', 'New Sort Asc'),
    },
    {
      name: 'newSortDesc',
      itemIconName: 'SortDesc',
      menuItemText: t('sort-new-desc', 'New Sort Desc'),
    },
  ]);
  const removeSortMenuItems = useRef<Array<OdinMenuItem>>([
    { name: 'removeSort', itemIconName: 'X', menuItemText: t('sort-remove', 'Remove Sort Field') },
    { name: 'removeAllSort', itemIconName: 'X', menuItemText: t('sort-remove-all', 'Remove All Sort Fields') },
  ]);
  const [columnMenuItems, setColumnMenuItems] = useState<Array<OdinMenuItem>>([]);
  const [columnSort, setColumnSort] = useState<ContinuousScrollerSort>();

  const tagCreator = useTestTag(column.title || '');

  useEffect(() => {
    let columnWidthValue = column.width;
    if (columnWidthValue === undefined) {
      const partialColumn = {
        type: column.type,
        customDataProperties: { fieldType: column.customDataProperties?.fieldType },
      };
      columnWidthValue = getColumnDefaultWidth(partialColumn);
    }
    setStartingColumnWidth(columnWidthValue);
    setColumnWidth(columnWidthValue);
  }, [column.width, column.type, column.customDataProperties?.fieldType]);

  // Force sync local header state with global column state after signal updates
  useEffect(() => {
    if (!isDragging && column.width !== undefined && columnWidth !== column.width) {
      setColumnWidth(column.width);
    }
  }, [column.width, columnWidth, isDragging]);

  useSignalEffect(() => {
    if (!column.disableSort) {
      const sortedField = sortSignal.value.find((field) => field.scrollerId === column.id);
      if (sortedField) {
        const newItems: Array<OdinMenuItem> = [];
        if (sortedField.sortDirection === ContinuousScrollerSortDirection.Ascending) {
          newItems.push(sortDescMenuItem.current);
        } else {
          newItems.push(sortAscMenuItem.current);
        }
        newItems.push(...newSortMenuItems.current);
        newItems.push(...removeSortMenuItems.current);

        setColumnMenuItems(newItems);
      } else if (sortSignal.value.length > 0) {
        setColumnMenuItems([...addSortMenuItems.current, ...newSortMenuItems.current]);
      } else {
        setColumnMenuItems([sortAscMenuItem.current, sortDescMenuItem.current]);
      }

      if (!isEqual(sortedField, columnSort)) {
        setColumnSort(sortedField);
      }
    }
  });

  const onDragStart = (event: MouseEvent) => {
    setIsDragging(true);
    setStartingColumnWidth(columnWidth); // Reset baseline to current actual width
    dragStartPosition = event.pageX;
    mouseCurrentPosition = dragStartPosition;

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onDragEnd);

    onColumnDragStart?.(column);
  };

  // the globalThis type below is a TypeScript reference to the global object...this is needed to identify this is the
  // DOM MouseEvent not Reacts version.
  const onMouseMove = throttle((event: globalThis.MouseEvent) => {
    mouseCurrentPosition = event.pageX;
    const changeAmount = mouseCurrentPosition - dragStartPosition;
    const currentColumnWidth = startingColumnWidth + changeAmount;
    const clampedWidth = Math.max(ABSOLUTE_MIN_COLUMN_WIDTH_PX, currentColumnWidth);
    setColumnWidth(clampedWidth);
  }, 16);

  // the globalThis type below is a TypeScript reference to the global object...this is needed to identify this is the
  // DOM MouseEvent not Reacts version.
  const onDragEnd = debounce((event: globalThis.MouseEvent) => {
    mouseCurrentPosition = event.pageX;
    const changeAmount = mouseCurrentPosition - dragStartPosition;
    const currentColumnWidth = startingColumnWidth + changeAmount;
    const clampedWidth = Math.max(ABSOLUTE_MIN_COLUMN_WIDTH_PX, currentColumnWidth);
    setStartingColumnWidth(clampedWidth);
    const newColumn: DataGridColumnSettings = {
      ...column,
      width: clampedWidth,
    };

    changeSignalColumnSize(columns, rowWidth, indicatorRowWidth, availableWidth, [newColumn], availableWidth.value);

    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onDragEnd);
    setIsDragging(false);

    onColumnDragEnd?.(newColumn);
  }, 100);

  const isRegularSort = (name: string) => name === 'sortAsc' || name === 'sortDesc';

  const isNewSort = (name: string) => name === 'newSortAsc' || name === 'newSortDesc';

  const isAddSort = (name: string) => name === 'addSortAsc' || name === 'addSortDesc';

  const isAscending = (name: string) => name === 'sortAsc' || name === 'newSortAsc' || name === 'addSortAsc';

  const onMenuItemClick = (name: string) => {
    if (isRegularSort(name)) {
      addSort(name);
    } else if (isNewSort(name)) {
      sortSignal.value = [
        {
          scrollerId: column.id,
          field: column.field,
          sortPath: column.sortPath,
          customDataProperties: column.customDataProperties,
          sortDirection: isAscending(name)
            ? ContinuousScrollerSortDirection.Ascending
            : ContinuousScrollerSortDirection.Descending,
          order: 1,
        },
      ];
    } else if (isAddSort(name)) {
      addSort(name);
    } else if (name === 'removeSort') {
      if (sortSignal.value.length > 0) {
        sortSignal.value = sortSignal.value
          .filter((field) => field.scrollerId !== column.id)
          .map((sort, index): ContinuousScrollerSort => {
            return {
              ...sort,
              order: index + 1,
            };
          });
      }
    } else {
      sortSignal.value = [];
    }
  };

  const addSort = useCallback(
    (name: string) => {
      const sortIndex = sortSignal.value.findIndex((sort) => sort.scrollerId === column.id);
      const clonedSorts = cloneDeep(sortSignal.value);
      if (sortIndex > -1) {
        const foundSort = clonedSorts[sortIndex];
        foundSort.sortDirection = isAscending(name)
          ? ContinuousScrollerSortDirection.Ascending
          : ContinuousScrollerSortDirection.Descending;
        foundSort.customDataProperties = column.customDataProperties;

        sortSignal.value = clonedSorts;
      } else {
        const nextIndex = sortSignal.value.length + 1;
        clonedSorts.push({
          scrollerId: column.id,
          field: column.field,
          sortPath: column.sortPath,
          customDataProperties: column.customDataProperties,
          sortDirection: isAscending(name)
            ? ContinuousScrollerSortDirection.Ascending
            : ContinuousScrollerSortDirection.Descending,
          order: nextIndex,
        });
        sortSignal.value = clonedSorts;
      }
    },
    [column.id, column.field, column.customDataProperties, column.sortPath]
  );

  const filterComponent = useMemo(() => {
    switch (column.searchType) {
      case 'TextSearch':
        return (
          <DataGridSearchFilter
            column={column}
            filterSignal={filterSignal}
            scrollDetection={scrollDetection}
            id={columnId}
            forceResetColumnMap={forceResetColumnMap}
          />
        );
      case 'MultiSelect':
        return (
          <DataGridDropDownFilter
            columns={columns}
            search={search}
            column={column}
            filterSignal={filterSignal}
            scrollDetection={scrollDetection}
            forceResetColumnMap={forceResetColumnMap}
          />
        );
      case 'DateRange':
        return (
          <DataGridDateFilter
            column={column}
            filterSignal={filterSignal}
            scrollDetection={scrollDetection}
            forceResetColumnMap={forceResetColumnMap}
          />
        );
      case 'Integer':
        return (
          <DataGridNumericFilter
            column={column}
            filterSignal={filterSignal}
            scrollDetection={scrollDetection}
            forceResetColumnMap={forceResetColumnMap}
          />
        );
      case 'Decimal':
        return (
          <DataGridNumericFilter
            type="decimal"
            column={column}
            filterSignal={filterSignal}
            scrollDetection={scrollDetection}
            forceResetColumnMap={forceResetColumnMap}
          />
        );
      default:
        return <></>;
    }
  }, [column, columnId, columns, filterSignal, forceResetColumnMap, scrollDetection, search]);

  const renderSortIndicator = () => {
    if (columnSort) {
      return columnSort.sortDirection === ContinuousScrollerSortDirection.Ascending ? (
        <OdinIcon icon="ChevronUp" size={OdinIconSize.ExtraSmall} />
      ) : (
        <OdinIcon icon="ChevronDown" size={OdinIconSize.ExtraSmall} />
      );
    }

    return null;
  };

  const labelComponent = () => {
    const className = 'shrink pr-2 truncate';
    const textValue = column.title || '';

    if (column.searchType === 'TextSearch') {
      return (
        <label className={className} htmlFor={columnId}>
          {textValue}
        </label>
      );
    }

    return <p className={className}>{textValue}</p>;
  };

  const sortStyles = clsx('shrink', {
    'pr-2': columnSort,
  });

  return (
    <div className="flex h-full group/header bg-gray-5 hover:bg-gray-4 transition-colors duration-200">
      <div
        style={{ width: columnWidth + 'px' }}
        className={clsx('column-main group flex flex-col p-2 font-bold', {
          'cursor-move': draggable,
          'cursor-default': !draggable,
        })}
        data-draggable={draggable}
        ref={tagCreator('column')}
      >
        <div
          className="mb-1 flex shrink items-center text-left text-xxs leading-3 justify-between"
          style={{ minHeight: '16px' }}
        >
          {labelComponent()}
          <div className="flex items-center">
            <div className="shrink h-4 transition-all duration-200">{renderSortIndicator()}</div>
            <div className={sortStyles}>{columnSort?.order}</div>

            {columnMenuItems.length > 0 && (
              <OdinIconMenu menuIconName="Menu" menuItems={columnMenuItems} onItemClick={onMenuItemClick} />
            )}
          </div>
        </div>
        <div className="grow text-sm font-normal">{filterComponent}</div>
      </div>
      <div className="flex items-center" style={{ cursor: 'col-resize' }} onMouseDown={(event) => onDragStart(event)}>
        <span className="p-1.5 h-12 shadow-gray-4 shadow-[1px_0px_0px_0px] z-20 group-hover/header:h-full" />
      </div>
    </div>
  );
}
